import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PopupWrapper from '../../../../components/popup/PopupWrapper';
import { getButtonClass } from '../../../../components/popup/popupUtils';
import { sanitizeHtml } from '../../../../assets/utils/comm/sanitize';
import { formatAllianceMemberRoleLabel } from '../alli/members/allianceMemberRoleLabels';
import { isAllianceMemberAccessSuspended } from '../alli/members/allianceMemberAccess';

/** 전역 openConfirm은 단일 팝업이라 이 모달을 교체하므로, TextPopup과 동일한 UI를 모달 내부에 띄움 */
const ROLE_OPTIONS = [
  { key: 'Consortium Owner', descriptionKey: 'popup.consortiumMemberEdit.roleOwnerDesc' },
  { key: 'PI', descriptionKey: 'popup.consortiumMemberEdit.rolePiDesc' },
  { key: 'Researcher', descriptionKey: 'popup.consortiumMemberEdit.roleResearcherDesc' },
  { key: 'Observer', descriptionKey: 'popup.consortiumMemberEdit.roleObserverDesc' },
];

const FIELD_LABEL_CLASS = 'form-label small text-muted mb-1';

/** API `userId`(로그인 계정 ID). 없을 때만 `userUid` 숫자 폴백 */
const getMemberUserIdDisplay = (m) => {
  if (!m || typeof m !== 'object') return '-';
  const loginId = m.userId ?? m.raw?.userId;
  if (loginId != null && String(loginId).trim() !== '') return String(loginId).trim();
  const uid = m.userUid ?? m.raw?.userUid;
  if (uid != null && String(uid).trim() !== '') return String(uid);
  return '-';
};

/**
 * 컨소시엄 멤버 정보 수정
 * config.initialData:
 *   - rowKind: 'accepted' | 'invited' | 'archive'
 *   - member (accepted|invited)
 *   - archiveItem (archive)
 *   - isOnlyOwner, canChangeRole, canManageAll, suspendEligible, currentUserUid (optional, for labels)
 *
 * onClose(submit, { selectedRole, accessSuspended, archiveRestore })
 * accessSuspended: true = 일시 중지 적용됨
 */
const ConsortiumMemberEditPopup = ({ config, onClose }) => {
  const { t } = useTranslation();
  const {
    rowKind,
    member,
    archiveItem,
    isOnlyOwner,
    canChangeRole,
    canManageAll,
    suspendEligible,
  } = config?.initialData || {};

  const [selectedRole, setSelectedRole] = useState(member?.role || '');
  /** 스위치 ON = 참여(접근 허용), OFF = 일시 중지 적용 */
  const [participationOn, setParticipationOn] = useState(true);
  const [blockMessage, setBlockMessage] = useState('');
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [toggleConfirmOpen, setToggleConfirmOpen] = useState(false);
  const [pendingParticipationOn, setPendingParticipationOn] = useState(null);

  useEffect(() => {
    if (member?.role) setSelectedRole(member.role);
    setParticipationOn(!isAllianceMemberAccessSuspended(member));
    setBlockMessage('');
    setSaveConfirmOpen(false);
    setToggleConfirmOpen(false);
    setPendingParticipationOn(null);
  }, [member]);

  useEffect(() => {
    if (!saveConfirmOpen) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setSaveConfirmOpen(false);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [saveConfirmOpen]);

  const handleCancel = () => onClose(false);

  const handleConfirmArchive = () => {
    onClose(true, { archiveRestore: true, archiveItem });
  };

  const saveConfirmContent =
    `<p>${t('popup.consortiumMemberEdit.saveConfirmQuestion')}</p>`
    + `<p class="small text-muted mb-0">${t('popup.consortiumMemberEdit.saveConfirmHint')}</p>`;
  const participationOnConfirmContent =
    `<p>${t('popup.consortiumMemberEdit.switchToActiveQuestion')}</p>`
    + `<p class="small text-muted mb-0">${t('popup.consortiumMemberEdit.switchConfirmHint')}</p>`;
  const participationOffConfirmContent =
    `<p>${t('popup.consortiumMemberEdit.switchToPausedQuestion')}</p>`
    + `<p class="small text-muted mb-0">${t('popup.consortiumMemberEdit.switchConfirmHint')}</p>`;

  const validateBeforeSave = () => {
    if (!member) return false;

    if (member.role === 'Consortium Owner' && selectedRole !== 'Consortium Owner') {
      if (isOnlyOwner) {
        setBlockMessage(t('popup.consortiumMemberEdit.lastOwnerError'));
        return false;
      }
    }
    return true;
  };

  const handleSaveClick = () => {
    if (!validateBeforeSave()) return;
    setSaveConfirmOpen(true);
  };

  const handleSaveConfirmCancel = () => setSaveConfirmOpen(false);

  const handleParticipationToggleChange = (nextChecked) => {
    if (!statusSwitchEnabled || nextChecked === participationOn) return;
    setPendingParticipationOn(nextChecked);
    setToggleConfirmOpen(true);
  };

  const handleToggleConfirmCancel = () => {
    setToggleConfirmOpen(false);
    setPendingParticipationOn(null);
  };

  const handleToggleConfirmApply = () => {
    if (typeof pendingParticipationOn === 'boolean') {
      setParticipationOn(pendingParticipationOn);
      setBlockMessage('');
    }
    setToggleConfirmOpen(false);
    setPendingParticipationOn(null);
  };

  const applySave = () => {
    if (!member) return;
    const accessSuspended = !participationOn;
    const suspendAppliedInPopup = Boolean(canManageAll && suspendEligible && rowKind === 'accepted');
    onClose(true, {
      selectedRole,
      accessSuspended,
      member,
      rowKind,
      suspendAppliedInPopup,
    });
  };

  if (rowKind === 'archive') {
    if (!archiveItem) return null;
    return (
      <PopupWrapper
        config={{
          ...config,
          title: t('popup.consortiumMemberEdit.title'),
          width: config?.width ?? 480,
          leftButton: { text: t('popup.common.cancel'), color: 'btn-secondary', submit: false },
          rightButton: { text: t('popup.consortiumMemberEdit.restore'), color: 'btn-primary', submit: true },
        }}
        onClose={onClose}
        displayType={config?.displayType || 'modal'}
        className="popup-consortium-member-edit"
      >
        <div className="main-popup-body">
          <p className="small text-muted mb-3">{t('popup.consortiumMemberEdit.subtitle')}</p>
          <div className="mb-3">
            <label className={FIELD_LABEL_CLASS}>{t('popup.common.name')}</label>
            <input type="text" className="form-control bg-light" readOnly value={archiveItem.name || ''} />
          </div>
          <div className="mb-3">
            <label className={FIELD_LABEL_CLASS}>{t('popup.common.organization')}</label>
            <input type="text" className="form-control bg-light" readOnly value={archiveItem.orgName || ''} />
          </div>
          <p className="small text-secondary mb-0">
            {t('popup.consortiumMemberEdit.archiveReason')}: <strong>{archiveItem.archiveReason || '-'}</strong>
            <br />
            {t('popup.consortiumMemberEdit.restoreHint')}
          </p>
        </div>
        <div className="main-popup-footer d-flex justify-content-between">
          <button type="button" className={`btn main-popup-left-btn ${getButtonClass('btn-secondary')}`} onClick={handleCancel}>
            {t('popup.common.cancel')}
          </button>
          <button type="button" className={`btn main-popup-right-btn ${getButtonClass('btn-primary')}`} onClick={handleConfirmArchive}>
            {t('popup.consortiumMemberEdit.restore')}
          </button>
        </div>
      </PopupWrapper>
    );
  }

  if (!member) return null;

  const showSuspendSwitch = canManageAll && suspendEligible && rowKind === 'accepted';
  const statusSwitchEnabled = showSuspendSwitch;
  const memberUserIdDisplay = getMemberUserIdDisplay(member);

  /** 일시 중지(폼상 스위치 OFF)인 참여 멤버는 역할 변경 불가 — 스위치로 참여로 바꾸면 다시 선택 가능 */
  const rolesLockedBySuspension = rowKind === 'accepted' && !participationOn;

  const isRoleOptionDisabled = (roleKey) => {
    if (rolesLockedBySuspension) return true;
    if (!canChangeRole) return true;
    if (member.role === 'Consortium Owner' && isOnlyOwner && roleKey !== 'Consortium Owner') return true;
    return false;
  };

  return (
    <PopupWrapper
      config={{
        ...config,
        title: t('popup.consortiumMemberEdit.title'),
        width: config?.width ?? 520,
        leftButton: { text: t('popup.common.cancel'), color: 'btn-secondary', submit: false },
        rightButton: { text: t('popup.common.saveChanges'), color: 'btn-primary', submit: true },
      }}
      onClose={onClose}
      displayType={config?.displayType || 'modal'}
      className="popup-consortium-member-edit"
    >
      <div className="position-relative">
      <div className="main-popup-body">
        <p className="small text-muted mb-3">{t('popup.consortiumMemberEdit.subtitle')}</p>

        <div className="mb-3">
          <label className={FIELD_LABEL_CLASS} htmlFor="consortium-member-edit-name">
            {t('popup.common.name')}
          </label>
          <input
            id="consortium-member-edit-name"
            type="text"
            className="form-control bg-light"
            readOnly
            value={member.name || ''}
          />
        </div>
        <div className="mb-3">
          <div className="row g-2 align-items-end">
            <div className="col-md-6">
              <label className={FIELD_LABEL_CLASS} htmlFor="consortium-member-edit-user-id">
                {t('popup.consortiumMemberEdit.userIdLabel')}
              </label>
              <input
                id="consortium-member-edit-user-id"
                type="text"
                className="form-control bg-light"
                readOnly
                value={memberUserIdDisplay}
                title={t('popup.consortiumMemberEdit.userIdTitle')}
              />
            </div>
            <div className="col-md-6">
              <label className={FIELD_LABEL_CLASS} htmlFor="consortium-member-edit-org">
                {t('popup.common.organization')}
              </label>
              <input
                id="consortium-member-edit-org"
                type="text"
                className="form-control bg-light"
                readOnly
                value={member.orgName || '-'}
              />
            </div>
          </div>
        </div>

        {rowKind === 'accepted' ? (
          <div className="mb-3">
            <label className={FIELD_LABEL_CLASS} htmlFor="consortium-member-participation-switch">
              {t('popup.consortiumMemberEdit.statusLabel')}
            </label>
            <div
              className={`d-flex align-items-center justify-content-between gap-3 rounded border bg-light px-3 py-2 ${statusSwitchEnabled ? '' : 'opacity-75'}`}
            >
              <span className="small text-muted mb-0">
                {participationOn ? t('popup.consortiumMemberEdit.statusActive') : t('popup.consortiumMemberEdit.statusPaused')}
                {!canManageAll ? (
                  <span className="d-block mt-1">{t('popup.consortiumMemberEdit.noSuspendPermission')}</span>
                ) : !statusSwitchEnabled ? (
                  member.role === 'Consortium Owner' ? (
                    <span className="d-block mt-1">{t('popup.consortiumMemberEdit.ownerNotSuspendable')}</span>
                  ) : (
                    <span className="d-block mt-1">{t('popup.consortiumMemberEdit.memberSuspendLocked')}</span>
                  )
                ) : null}
              </span>
              {/* Bootstrap form-switch는 전역 checkbox 스타일과 겹쳐 썸이 이중으로 보일 수 있어 커스텀 토글 사용 */}
              <input
                type="checkbox"
                id="consortium-member-participation-switch"
                className="visually-hidden consortium-member-edit-toggle-input"
                checked={participationOn}
                disabled={!statusSwitchEnabled}
                onChange={(e) => handleParticipationToggleChange(e.target.checked)}
                aria-label={participationOn ? t('popup.consortiumMemberEdit.switchAriaOn') : t('popup.consortiumMemberEdit.switchAriaOff')}
              />
              <label
                htmlFor="consortium-member-participation-switch"
                className={`consortium-member-edit-toggle-ui mb-0 ${statusSwitchEnabled ? '' : 'consortium-member-edit-toggle-ui--disabled'}`}
              >
                <span className="consortium-member-edit-toggle-track" aria-hidden>
                  <span className="consortium-member-edit-toggle-thumb" />
                </span>
              </label>
            </div>
          </div>
        ) : null}

        <div className="mb-2">
          <label className={`${FIELD_LABEL_CLASS} d-block`}>{t('popup.consortiumMemberEdit.roleLabel')}</label>
          {rolesLockedBySuspension ? (
            <p className="small text-muted mb-2">{t('popup.consortiumMemberEdit.roleLockedBySuspension')}</p>
          ) : null}
          <div className={`border rounded-3 p-2 bg-white ${rolesLockedBySuspension ? 'opacity-75' : ''}`}>
            {ROLE_OPTIONS.map((opt) => {
              const checked = selectedRole === opt.key;
              const disabled = isRoleOptionDisabled(opt.key);
              return (
                <label
                  key={opt.key}
                  className={`d-flex align-items-start gap-2 p-2 rounded mb-1 ${disabled ? 'opacity-50' : 'cursor-pointer'}`}
                >
                  <input
                    type="radio"
                    className="form-check-input mt-1 flex-shrink-0"
                    name="consortium-member-role"
                    checked={checked}
                    disabled={disabled}
                    onChange={() => {
                      setSelectedRole(opt.key);
                      setBlockMessage('');
                    }}
                  />
                  <span className="flex-grow-1">
                    <span className="fw-medium d-block">{formatAllianceMemberRoleLabel(opt.key)}</span>
                    <span className="small text-muted d-block">{t(opt.descriptionKey)}</span>
                  </span>
                </label>
              );
            })}
          </div>
        </div>

        {selectedRole === 'Consortium Owner' && member.role !== 'Consortium Owner' && !isOnlyOwner && canChangeRole ? (
          <p className="small text-info mb-2">{t('popup.consortiumMemberEdit.ownerChangeNotice')}</p>
        ) : null}
        {blockMessage ? <p className="small text-danger mb-0">{blockMessage}</p> : null}
      </div>
      <div className="main-popup-footer d-flex justify-content-between">
        <button type="button" className={`btn main-popup-left-btn ${getButtonClass('btn-secondary')}`} onClick={handleCancel}>
          {t('popup.common.cancel')}
        </button>
        <button type="button" className={`btn main-popup-right-btn ${getButtonClass('btn-primary')}`} onClick={handleSaveClick}>
          {t('popup.common.saveChanges')}
        </button>
      </div>
      {saveConfirmOpen ? (
        <div
          className="consortium-member-save-confirm-overlay"
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="consortium-member-save-confirm-title"
          onClick={(e) => {
            if (e.target === e.currentTarget) handleSaveConfirmCancel();
          }}
        >
          <div className="consortium-member-save-confirm-panel popup-text shadow-sm" onClick={(e) => e.stopPropagation()}>
            <h4 id="consortium-member-save-confirm-title" className="h6 mb-3">
              {t('popup.common.confirm')}
            </h4>
            <div
              className="main-popup-body popup-text p-0 mb-0"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(saveConfirmContent) }}
            />
            <div className="main-popup-footer d-flex justify-content-between mt-3 mb-0 pt-0 border-0">
              <button
                type="button"
                className={`btn main-popup-left-btn ${getButtonClass('btn-secondary')}`}
                onClick={handleSaveConfirmCancel}
              >
                {t('popup.common.cancel')}
              </button>
              <button type="button" className={`btn main-popup-right-btn ${getButtonClass('btn-primary')}`} onClick={applySave}>
                {t('popup.common.confirm')}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {toggleConfirmOpen ? (
        <div
          className="consortium-member-save-confirm-overlay"
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="consortium-member-toggle-confirm-title"
          onClick={(e) => {
            if (e.target === e.currentTarget) handleToggleConfirmCancel();
          }}
        >
          <div className="consortium-member-save-confirm-panel popup-text shadow-sm" onClick={(e) => e.stopPropagation()}>
            <h4 id="consortium-member-toggle-confirm-title" className="h6 mb-3">
              {t('popup.consortiumMemberEdit.statusChangeTitle')}
            </h4>
            <div
              className="main-popup-body popup-text p-0 mb-0"
              dangerouslySetInnerHTML={{
                __html: sanitizeHtml(
                  pendingParticipationOn === true
                    ? participationOnConfirmContent
                    : participationOffConfirmContent,
                ),
              }}
            />
            <div className="main-popup-footer d-flex justify-content-between mt-3 mb-0 pt-0 border-0">
              <button
                type="button"
                className={`btn main-popup-left-btn ${getButtonClass('btn-secondary')}`}
                onClick={handleToggleConfirmCancel}
              >
                {t('popup.common.cancel')}
              </button>
              <button type="button" className={`btn main-popup-right-btn ${getButtonClass('btn-primary')}`} onClick={handleToggleConfirmApply}>
                {t('popup.common.confirm')}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      </div>
      <style>{`
        .popup-consortium-member-edit .consortium-member-edit-toggle-input:focus-visible + .consortium-member-edit-toggle-ui .consortium-member-edit-toggle-track {
          outline: 2px solid #0d6efd;
          outline-offset: 2px;
        }
        .popup-consortium-member-edit .consortium-member-edit-toggle-ui {
          display: inline-flex;
          flex-shrink: 0;
          cursor: pointer;
          user-select: none;
        }
        .popup-consortium-member-edit .consortium-member-edit-toggle-ui--disabled {
          cursor: not-allowed;
          opacity: 0.65;
          pointer-events: none;
        }
        .popup-consortium-member-edit .consortium-member-edit-toggle-track {
          position: relative;
          display: block;
          width: 2.5rem;
          height: 1.45rem;
          border-radius: 999px;
          background: #cbd5e1;
          box-sizing: border-box;
          border: 1px solid rgba(0, 0, 0, 0.06);
          transition: background-color 0.2s ease;
        }
        .popup-consortium-member-edit .consortium-member-edit-toggle-input:checked + .consortium-member-edit-toggle-ui .consortium-member-edit-toggle-track {
          background: #22c55e;
          border-color: transparent;
        }
        .popup-consortium-member-edit .consortium-member-edit-toggle-thumb {
          position: absolute;
          top: 3px;
          left: 3px;
          width: calc(1.25rem - 6px);
          height: calc(1.25rem - 6px);
          border-radius: 50%;
          background: #fff;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
          transition: left 0.2s ease, transform 0.2s ease;
          pointer-events: none;
        }
        .popup-consortium-member-edit .consortium-member-edit-toggle-input:checked + .consortium-member-edit-toggle-ui .consortium-member-edit-toggle-thumb {
          left: calc(100% - 3px);
          transform: translateX(-100%);
        }
        .popup-consortium-member-edit .consortium-member-save-confirm-overlay {
          position: absolute;
          inset: 0;
          z-index: 20;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 1rem;
          background: rgba(0, 0, 0, 0.45);
        }
        .popup-consortium-member-edit .consortium-member-save-confirm-panel {
          width: 100%;
          max-width: 400px;
          background: #fff;
          border-radius: 8px;
          padding: 1.25rem 1.25rem 1rem;
        }
      `}</style>
    </PopupWrapper>
  );
};

export default ConsortiumMemberEditPopup;
